import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { PROFESSIONS, subscriptionStatusView, timeAgoPt, trialDaysLeft } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Settings() {
    const { user, refresh, logout } = useAuth();
    const [form, setForm] = useState({
        name: user?.name || "", phone: user?.phone || "", company: user?.company || "",
        crea: user?.crea || "", profession: user?.profession || "",
    });
    const [saving, setSaving] = useState(false);
    const [digestOn, setDigestOn] = useState(user?.regulatory_digest_email !== false);
    const [dailyOn, setDailyOn] = useState(user?.deadline_daily_digest === true);
    const [weeklyOn, setWeeklyOn] = useState(user?.deadline_weekly_digest === true);

    const save = async () => {
        setSaving(true);
        try { await api.put("/auth/profile", form); await refresh(); toast.success("Dados atualizados."); }
        catch (e) { toast.error("Erro ao salvar."); } finally { setSaving(false); }
    };

    const toggleDigest = async (val) => {
        setDigestOn(val);
        try { await api.put("/auth/profile", { regulatory_digest_email: val }); await refresh(); toast.success(val ? "Radar Regulatório ativado." : "Radar Regulatório desativado."); }
        catch (e) { setDigestOn(!val); toast.error("Erro ao salvar preferência."); }
    };

    const toggleDaily = async (val) => {
        setDailyOn(val);
        try { await api.put("/auth/profile", { deadline_daily_digest: val }); await refresh(); toast.success(val ? "Resumo diário de prazos ativado." : "Resumo diário desativado."); }
        catch (e) { setDailyOn(!val); toast.error("Erro ao salvar preferência."); }
    };

    const toggleWeekly = async (val) => {
        setWeeklyOn(val);
        try { await api.put("/auth/profile", { deadline_weekly_digest: val }); await refresh(); toast.success(val ? "Resumo semanal de prazos ativado." : "Resumo semanal desativado."); }
        catch (e) { setWeeklyOn(!val); toast.error("Erro ao salvar preferência."); }
    };

    const deleteAccount = async () => {
        try { await api.delete("/auth/account"); toast.success("Conta excluída."); logout(); }
        catch (e) { toast.error("Erro ao excluir conta."); }
    };

    return (
        <div className="max-w-2xl space-y-6" data-testid="settings-page">
            <PageHeader title="Configurações" subtitle="Gerencie seus dados pessoais e sua conta." />

            <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm space-y-4">
                <h2 className="font-heading font-bold text-slate-800">Dados pessoais</h2>
                <div><Label className="mb-1 block">Nome</Label><Input data-testid="settings-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="mb-1 block">E-mail</Label><Input value={user?.email} disabled className="bg-slate-50" /></div>
                    <div><Label className="mb-1 block">Telefone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                    <div><Label className="mb-1 block">Empresa</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
                    <div><Label className="mb-1 block">CREA</Label><Input value={form.crea} onChange={(e) => setForm({ ...form, crea: e.target.value })} /></div>
                    <div>
                        <Label className="mb-1 block">Profissão</Label>
                        <Select value={form.profession} onValueChange={(v) => setForm({ ...form, profession: v })}>
                            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                            <SelectContent>{PROFESSIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>
                <button onClick={save} disabled={saving} data-testid="settings-save" className="bg-petroleum hover:bg-petroleumHover text-white rounded-sm px-6 py-2 font-semibold transition-colors disabled:opacity-60">{saving ? "Salvando..." : "Salvar alterações"}</button>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm" data-testid="account-status-card">
                <h2 className="font-heading font-bold text-slate-800">Conta</h2>
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                    <div>
                        <span className="text-xs uppercase tracking-wide text-slate-400 block mb-1">Status da assinatura</span>
                        {(() => {
                            const sm = subscriptionStatusView(user);
                            return <span data-testid="account-status" className={`text-sm font-semibold px-3 py-1 rounded-full border ${sm.cls}`}>{sm.label}</span>;
                        })()}
                        {user?.subscription_status === "trial" && user?.trial_ends_at && (() => {
                            const dl = trialDaysLeft(user);
                            return <p className="text-xs text-slate-400 mt-1.5">Teste termina em {new Date(user.trial_ends_at).toLocaleDateString("pt-BR")}{dl != null && dl > 0 ? <span className="text-blue-500 font-semibold"> · {dl === 1 ? "falta 1 dia" : `faltam ${dl} dias`}</span> : <span className="text-red-500 font-semibold"> · teste encerrado</span>}</p>;
                        })()}
                    </div>
                    <div>
                        <span className="text-xs uppercase tracking-wide text-slate-400 block mb-1">Cliente desde</span>
                        <b className="text-slate-800 text-sm" data-testid="account-since">
                            {user?.created_at ? new Date(user.created_at).toLocaleDateString("pt-BR") : "—"}
                        </b>
                        <span className="text-xs text-slate-400 ml-2">{timeAgoPt(user?.created_at)}</span>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm">
                <h2 className="font-heading font-bold text-slate-800">Notificações por e-mail</h2>
                <div className="mt-4 flex items-start justify-between gap-4">
                    <div>
                        <div className="font-semibold text-slate-800 text-sm">Radar Regulatório (resumo mensal)</div>
                        <p className="text-sm text-slate-500 mt-1">Receba, no início de cada mês, um e-mail com todas as novas normas da ANM adicionadas à base de Legislação. As notificações no sino continuam ativas.</p>
                    </div>
                    <Switch checked={digestOn} onCheckedChange={toggleDigest} data-testid="toggle-regulatory-digest" />
                </div>
                <div className="mt-5 pt-4 border-t border-slate-100 flex items-start justify-between gap-4">
                    <div>
                        <div className="font-semibold text-slate-800 text-sm">Resumo diário de prazos</div>
                        <p className="text-sm text-slate-500 mt-1">Receba um e-mail diário com seus prazos vencendo (próximos 7/30 dias), vencidos e pendentes de confirmação.</p>
                    </div>
                    <Switch checked={dailyOn} onCheckedChange={toggleDaily} data-testid="toggle-deadline-daily" />
                </div>
                <div className="mt-5 pt-4 border-t border-slate-100 flex items-start justify-between gap-4">
                    <div>
                        <div className="font-semibold text-slate-800 text-sm">Resumo semanal de prazos</div>
                        <p className="text-sm text-slate-500 mt-1">Prefere menos e-mails? Receba um único resumo às segundas-feiras com o panorama dos seus prazos.</p>
                    </div>
                    <Switch checked={weeklyOn} onCheckedChange={toggleWeekly} data-testid="toggle-deadline-weekly" />
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm">
                <h2 className="font-heading font-bold text-slate-800">Privacidade e consentimentos (LGPD)</h2>
                <p className="text-sm text-slate-500 mt-2">Você pode alterar seus dados pessoais acima, solicitar a exclusão dos dados e excluir sua conta. Ao usar a plataforma, você concorda com os Termos de Uso e a Política de Privacidade.</p>
            </div>

            <div className="bg-white border border-red-200 rounded-md p-6 shadow-sm">
                <h2 className="font-heading font-bold text-red-700">Zona de perigo</h2>
                <p className="text-sm text-slate-500 mt-2">A exclusão da conta remove permanentemente todos os seus processos, prazos, documentos e histórico.</p>
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <button className="mt-4 border-2 border-red-200 text-red-600 hover:bg-red-50 rounded-sm px-5 py-2 font-semibold transition-colors" data-testid="delete-account-button">Excluir minha conta</button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Excluir conta permanentemente?</AlertDialogTitle>
                            <AlertDialogDescription>Todos os seus dados serão apagados e esta ação não pode ser desfeita.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={deleteAccount} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-account">Excluir conta</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </div>
    );
}
